import { openVideo, type VideoSource } from '@/lib/videoSource';
import { putParts } from '@/lib/voltrVideo';
import { ApiError, NetworkError, RateLimitError, ServerError, TimeoutError } from '@/services/errors';
import type { PickedVideo } from '@/stores/voltrUploadTypes';
import type { VoltrUploadPart } from '@/types/voltr';

/** Opens the picked video, hands it to `work`, and always closes it. */
export async function withVideo<T>(video: PickedVideo, work: (source: VideoSource) => Promise<T>): Promise<T> {
  const source = await openVideo(video);
  try {
    return await work(source);
  } finally {
    source.close();
  }
}

/** Failed rounds in a row allowed after the first; a round that sends a part resets the count. */
export const PART_RETRY_ROUNDS = 2;
/** Wait before each retry round: a dropped connection needs a moment before asking again works. */
export const RETRY_BACKOFF_MS = [2_000, 5_000] as const;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Worth another round: storage refused a part, the connection dropped or stalled, or the server was busy. Our own 4xx isn't. */
function retryable(err: unknown): boolean {
  if (err instanceof ApiError) return err instanceof NetworkError || err instanceof TimeoutError || err instanceof ServerError || err instanceof RateLimitError;
  return true;
}

/**
 * Creates (or resumes) the job and PUTs its parts. When a round fails (an
 * expired URL, a dropped connection), it waits, then `create` runs again: the
 * same checksum resumes the job with fresh URLs, and the parts Voltr already
 * has are skipped. Gives up after PART_RETRY_ROUNDS failed rounds in a row (a
 * round that sent any part starts the count over, so one flaky stretch in a
 * long upload isn't fatal), at once on an error a retry can't fix, or when
 * `signal` stopped it. Resolves with the job id once every byte is sent.
 */
export async function sendParts(
  create: () => Promise<{ id: string; parts: VoltrUploadPart[] }>,
  source: VideoSource,
  onProgress: (sentBytes: number) => void,
  signal: AbortSignal,
): Promise<string> {
  for (let failures = 0; ; ) {
    let reports = 0;
    try {
      const upload = await create();
      await putParts(source, upload.parts, (sent) => ((reports += 1), onProgress(sent)), signal);
      return upload.id;
    } catch (err) {
      // putParts reports once before the first part, then after each part sent.
      if (reports > 1) failures = 0;
      if (signal.aborted || failures >= PART_RETRY_ROUNDS || !retryable(err)) throw err;
      await sleep(RETRY_BACKOFF_MS[failures] ?? 0);
      failures += 1;
      if (signal.aborted) throw err;
    }
  }
}
